/**
 * Video Player Component
 *
 * Custom video player with styled controls, buffering state, auto-hide functionality,
 * adaptive quality selection, and manual quality switcher.
 * Used within the file preview overlay for video content.
 */

import {
  Component,
  input,
  output,
  viewChild,
  ElementRef,
  signal,
  computed,
  effect,
  untracked,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  inject,
  NgZone,
  ChangeDetectionStrategy
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AnalyticsCollectorService } from '../../../../services/analytics-collector.service';
import {
  faPlay,
  faPause,
  faVolumeHigh,
  faVolumeLow,
  faVolumeXmark,
  faExpand,
  faLocationDot
} from '@fortawesome/pro-duotone-svg-icons';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslateModule } from '@ngx-translate/core';

import { ANNOTATION_DRAG_THRESHOLD_PX, VideoData, VideoFramePin, VideoStream, VideoTimecodeMarker } from '../../file-preview.model';
import { type Box, type Point } from '../../coordinate-mapping.utils';
import { formatTimecode } from '../../../../utils/timecode.utils';

@Component({
  selector: 'app-video-player',
  standalone: true,
  imports: [
    FontAwesomeModule,
    TranslateModule,
    TooltipModule,
    ProgressSpinnerModule
  ],
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoPlayerComponent implements OnDestroy, OnChanges, AfterViewInit {
  private readonly ngZone = inject(NgZone);

  // Icons
  readonly faPlay = faPlay;
  readonly faPause = faPause;
  readonly faVolumeHigh = faVolumeHigh;
  readonly faVolumeLow = faVolumeLow;
  readonly faVolumeXmark = faVolumeXmark;
  readonly faExpand = faExpand;
  readonly faLocationDot = faLocationDot;

  private readonly analytics = inject(AnalyticsCollectorService);

  // Inputs
  readonly videoData = input.required<VideoData>();
  readonly itemId = input('');
  readonly posterUrl = input('');
  readonly autoplay = input(true);
  readonly muted = input(false);
  /** Comment-anchor markers rendered on the progress bar (ranges render as strips).
   *  Default empty — the public lightbox and plain previews carry no comment chrome. */
  readonly markers = input<readonly VideoTimecodeMarker[]>([]);
  /** While true, the frame overlay takes the pointer and a point/region gesture on the
   *  paused frame produces a spatial timecode anchor. Arming pauses playback — an
   *  annotation marks the frame the user is looking at, not a moving target. */
  readonly annotateMode = input(false);
  /** TIMECODE anchors carrying on-frame geometry — rendered as pins/regions over the
   *  frame around their own moment. Default empty — plain previews carry no chrome. */
  readonly framePins = input<readonly VideoFramePin[]>([]);
  /** The captured-but-not-yet-posted mark: draft pin/region on the frame while the
   *  comment is written (clears together with the composer's chip). */
  readonly pendingFramePin = input<{ x: number; y: number; width?: number; height?: number } | null>(null);
  /** The frame pin whose thread chip is hovered/activated — mirrors the highlight. */
  readonly activePinId = input<string | null>(null);

  // Outputs
  readonly ended = output<void>();
  /** Fires ONCE per load on first play. The member file-preview leaves it unwired (the
   * internal member-collector emit above covers it); the PUBLIC lightbox — which passes no [itemId],
   * keeping that emit silent — wires it to the anonymous collector. Never both ⇒ never a double count. */
  readonly playStarted = output<{ duration?: number; quality?: string }>();
  /** Playback position, once per timeupdate — anchor renderers highlight the active marker off it. */
  readonly timeUpdate = output<number>();
  /** A completed annotate gesture on the frame: fractions of the FRAME + the moment it
   *  marks (the playhead at capture — the video is paused while annotating). */
  readonly annotationCapture = output<{ x: number; y: number; width?: number; height?: number; timecode: number }>();
  /** A frame pin was ACTIVATED — the host shows its comment in the thread. */
  readonly framePinClick = output<string>();

  // Video element reference
  readonly videoPlayerRef = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');
  readonly videoContainerRef = viewChild<ElementRef<HTMLDivElement>>('videoContainer');

  // State signals
  readonly isPlaying = signal(false);
  readonly currentTime = signal(0);
  readonly duration = signal(0);
  readonly volume = signal(1);
  readonly isMuted = signal(false);
  readonly isBuffering = signal(false);
  readonly controlsVisible = signal(true);

  // Quality switcher state
  readonly activeStream = signal<VideoStream | null>(null);
  readonly qualityMenuOpen = signal(false);

  private controlsTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Focus is inside the player's chrome — the auto-hide timer stands down (a keyboard member is using the bar). */
  private focusWithinControls = false;
  private pendingSeek: { time: number; wasPlaying: boolean } | null = null;
  private initialized = false;
  private playTracked = false;

  // ============================================================================
  // Frame annotations — geometry + gesture
  // ============================================================================

  /** The VIDEO CONTENT box inside the container (letterbox-aware, container space) —
   *  the frame overlay is positioned to it, so pins and gestures use plain fractions
   *  of the FRAME (the same fit math as the image overlay's base box). */
  readonly videoBox = signal<Box | null>(null);

  /** In-flight annotate gesture (frame fractions). */
  private readonly frameDraft = signal<{ start: Point; current: Point } | null>(null);
  private frameGestureDownScreen: { x: number; y: number } | null = null;
  private frameResizeObserver: ResizeObserver | null = null;

  /** Pins around the CURRENT moment only — an annotation belongs to its frame, not the
   *  whole clip. The half-second window absorbs seek landing error and timeupdate grain. */
  readonly visibleFramePins = computed<readonly VideoFramePin[]>(() => {
    const now = this.currentTime();
    return this.framePins().filter(p => Math.abs(p.timecode - now) <= 0.5);
  });

  /** The in-flight draft's visual rectangle in frame-PERCENT terms (clamped for display
   *  only — the capture math never clamps; releasing outside the frame cancels). */
  protected readonly frameDraftRect = computed<{ left: number; top: number; width: number; height: number } | null>(() => {
    const d = this.frameDraft();
    if (!d) return null;
    const clamp = (v: number): number => Math.max(0, Math.min(1, v));
    const x1 = clamp(Math.min(d.start.x, d.current.x));
    const y1 = clamp(Math.min(d.start.y, d.current.y));
    const x2 = clamp(Math.max(d.start.x, d.current.x));
    const y2 = clamp(Math.max(d.start.y, d.current.y));
    return { left: x1 * 100, top: y1 * 100, width: (x2 - x1) * 100, height: (y2 - y1) * 100 };
  });

  constructor() {
    // Arming annotate pauses playback — the gesture marks the frame the user is looking
    // at, and a stamp on a moving target would mark the wrong moment.
    effect(() => {
      if (!this.annotateMode()) return;
      untracked(() => {
        this.pauseVideo();
        this.measureVideoBox();
      });
    });

    // Keep the frame overlay's box true across element resizes (panel toggle, window
    // resize, fullscreen, quality switch). Observes the VIDEO element — the box of
    // interest — and re-attaches when the element remounts (stream swap). Feature-guarded:
    // jsdom specs have no ResizeObserver.
    effect(() => {
      const video = this.videoPlayerRef()?.nativeElement;
      untracked(() => {
        this.frameResizeObserver?.disconnect();
        this.frameResizeObserver = null;
        if (video && typeof ResizeObserver === 'function') {
          this.frameResizeObserver = new ResizeObserver(() =>
            this.ngZone.run(() => this.measureVideoBox()));
          this.frameResizeObserver.observe(video);
        }
      });
    });
  }

  /** Public pause — the host stamps a timestamp when the composer takes focus, and the
   *  stamp must equal the frame on screen. */
  pauseVideo(): void {
    this.videoPlayerRef()?.nativeElement?.pause();
  }

  /** Re-derive the frame box from the RENDERED video element's rect (container space).
   *  The element self-sizes with auto dimensions (aspect preserved — element box ≡ frame
   *  box), so measuring the real rect needs no assumptions about the sizing CSS; a
   *  fit-math derivation against the container would drift the moment the element's
   *  viewport-tied max-height diverges from the container height. */
  private measureVideoBox(): void {
    const container = this.videoContainerRef()?.nativeElement;
    const video = this.videoPlayerRef()?.nativeElement;
    if (!container || !video || !video.videoWidth || !video.videoHeight) {
      this.videoBox.set(null);
      return;
    }
    const c = container.getBoundingClientRect();
    const v = video.getBoundingClientRect();
    if (v.width <= 0 || v.height <= 0) {
      this.videoBox.set(null);
      return;
    }
    this.videoBox.set({ left: v.left - c.left, top: v.top - c.top, width: v.width, height: v.height });
  }

  private frameFraction(event: PointerEvent, overlay: HTMLElement): Point {
    const rect = overlay.getBoundingClientRect();
    return {
      x: rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0,
      y: rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0,
    };
  }

  protected onFramePointerDown(event: PointerEvent): void {
    if (!this.annotateMode()) return;
    const fraction = this.frameFraction(event, event.currentTarget as HTMLElement);
    this.frameGestureDownScreen = { x: event.clientX, y: event.clientY };
    this.frameDraft.set({ start: fraction, current: fraction });
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  protected onFramePointerMove(event: PointerEvent): void {
    if (!this.frameDraft()) return;
    const fraction = this.frameFraction(event, event.currentTarget as HTMLElement);
    this.frameDraft.update(d => (d ? { ...d, current: fraction } : d));
  }

  protected onFramePointerUp(event: PointerEvent): void {
    const draft = this.frameDraft();
    const down = this.frameGestureDownScreen;
    this.frameDraft.set(null);
    this.frameGestureDownScreen = null;
    if (!draft || !down) return;
    const release = this.frameFraction(event, event.currentTarget as HTMLElement);
    const inFrame = (p: Point): boolean => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1;
    // Released outside the frame = the gesture cancels. Never clamp — a clamped pin
    // would mark a place the author never pointed at (same rule as the image gesture
    // and the server's reject-never-clamp posture).
    if (!inFrame(release) || !inFrame(draft.start)) return;
    const timecode = Math.max(0, this.currentTime());
    const dragDistance = Math.hypot(event.clientX - down.x, event.clientY - down.y);
    if (dragDistance < ANNOTATION_DRAG_THRESHOLD_PX) {
      this.annotationCapture.emit({ x: draft.start.x, y: draft.start.y, timecode });
      return;
    }
    this.annotationCapture.emit({
      x: Math.min(draft.start.x, release.x),
      y: Math.min(draft.start.y, release.y),
      width: Math.abs(release.x - draft.start.x),
      height: Math.abs(release.y - draft.start.y),
      timecode,
    });
  }

  protected onFramePointerCancel(): void {
    this.frameDraft.set(null);
    this.frameGestureDownScreen = null;
  }

  // Computed signals
  readonly progress = computed(() => {
    const dur = this.duration();
    if (dur <= 0) return 0;
    return (this.currentTime() / dur) * 100;
  });

  readonly currentTimeFormatted = computed(() =>
    formatTimecode(this.currentTime())
  );

  readonly durationFormatted = computed(() =>
    formatTimecode(this.duration())
  );

  readonly volumeIcon = computed(() => {
    if (this.isMuted() || this.volume() === 0) {
      return this.faVolumeXmark;
    }
    return this.volume() < 0.5 ? this.faVolumeLow : this.faVolumeHigh;
  });

  readonly hasMultipleStreams = computed(() =>
    this.videoData()?.streams?.length > 1
  );

  ngAfterViewInit(): void {
    if (!this.initialized && this.videoData()?.streams?.length) {
      this.selectInitialStream();
      this.initialized = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['videoData'] && this.videoData()?.streams?.length) {
      this.selectInitialStream();
      this.initialized = true;
    }
  }

  ngOnDestroy(): void {
    this.clearControlsTimeout();
    this.frameResizeObserver?.disconnect();
    this.frameResizeObserver = null;
  }

  // ============================================================================
  // Quality Selection
  // ============================================================================

  /**
   * Pick the best stream for the current viewport on initial load.
   * Chooses the largest stream whose height fits within the container.
   */
  private selectInitialStream(): void {
    const streams = this.videoData().streams;
    if (!streams.length) return;

    const containerHeight = this.videoContainerRef()?.nativeElement?.clientHeight
      ?? window.innerHeight - 200;

    // Pick largest stream that fits, fallback to smallest
    let best = streams[0];
    for (const s of streams) {
      if (s.height <= containerHeight) {
        best = s;
      }
    }

    this.activeStream.set(best);
  }

  /**
   * Switch to a different quality stream.
   * Preserves playback position and play/pause state.
   */
  selectStream(stream: VideoStream): void {
    const video = this.videoPlayerRef()?.nativeElement;
    if (!video || stream === this.activeStream()) {
      this.qualityMenuOpen.set(false);
      return;
    }

    // Save current playback state
    this.pendingSeek = {
      time: video.currentTime,
      wasPlaying: !video.paused
    };

    const previousStream = this.activeStream();
    this.activeStream.set(stream);
    this.qualityMenuOpen.set(false);

    if (this.itemId() && previousStream) {
      this.analytics.videoQualitySwitched(this.itemId(), previousStream.label, stream.label);
    }
  }

  toggleQualityMenu(): void {
    this.qualityMenuOpen.update(v => !v);
  }

  // ============================================================================
  // Video Controls
  // ============================================================================

  togglePlay(): void {
    const video = this.videoPlayerRef()?.nativeElement;
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }

  onPlay(): void {
    this.isPlaying.set(true);
    this.startControlsHideTimer();

    if (!this.playTracked) {
      this.playTracked = true;
      const stream = this.activeStream();
      // Member surfaces pass [itemId] → the member collector records; the public lightbox does not,
      // and instead consumes playStarted. The playTracked gate covers both paths.
      if (this.itemId()) {
        this.analytics.videoPlayed(this.itemId(), this.videoData()?.duration, stream?.label);
      }
      this.playStarted.emit({ duration: this.videoData()?.duration, quality: stream?.label });
    }
  }

  onPause(): void {
    this.isPlaying.set(false);
    this.showControls();
  }

  onTimeUpdate(): void {
    const video = this.videoPlayerRef()?.nativeElement;
    if (video) {
      this.ngZone.run(() => {
        this.currentTime.set(video.currentTime);
        this.timeUpdate.emit(video.currentTime);
      });
    }
  }

  /**
   * Public API — anchor navigation (timecode chips/markers) seeks the playback head.
   * Clamped into [0, duration]; the signal updates immediately so dependent UI never
   * waits for the media element's own timeupdate.
   */
  seekTo(seconds: number): void {
    const clamped = Math.max(0, Math.min(seconds, this.duration() || 0));
    const video = this.videoPlayerRef()?.nativeElement;
    if (video) video.currentTime = clamped;
    this.currentTime.set(clamped);
  }

  /** The progress slider's arrow keys: a step relative to the playhead, clamped like every seek. */
  seekBy(seconds: number): void {
    this.seekTo(this.currentTime() + seconds);
  }

  /** Fraction of the duration [0..1] for progress-bar marker placement. */
  protected markerFraction(seconds: number): number {
    const dur = this.duration();
    if (dur <= 0) return 0;
    return Math.max(0, Math.min(1, seconds / dur));
  }

  onLoadedMetadata(): void {
    const video = this.videoPlayerRef()?.nativeElement;
    if (video) {
      this.duration.set(video.duration);
      this.measureVideoBox();

      // Apply muted setting
      video.muted = this.muted();
      this.isMuted.set(this.muted());

      // Handle pending seek from quality switch
      if (this.pendingSeek) {
        video.currentTime = this.pendingSeek.time;
        if (this.pendingSeek.wasPlaying) {
          video.play().catch(() => {});
        }
        this.pendingSeek = null;
        return;
      }

      // Auto-play after loading if enabled
      if (this.autoplay()) {
        video.play().catch(() => {
          // Auto-play may be blocked by browser policy
        });
      }
    }
  }

  onWaiting(): void {
    this.isBuffering.set(true);
  }

  onCanPlay(): void {
    this.isBuffering.set(false);
  }

  onEnded(): void {
    this.isPlaying.set(false);
    this.showControls();
    this.ended.emit();
  }

  seek(event: MouseEvent): void {
    const video = this.videoPlayerRef()?.nativeElement;
    const target = event.currentTarget as HTMLElement;
    if (!video || !target) return;

    const rect = target.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    video.currentTime = percent * video.duration;
  }

  setVolume(event: Event): void {
    const video = this.videoPlayerRef()?.nativeElement;
    const input = event.target as HTMLInputElement;
    if (!video || !input) return;

    const newVolume = parseFloat(input.value);
    video.volume = newVolume;
    this.volume.set(newVolume);

    if (newVolume > 0 && this.isMuted()) {
      video.muted = false;
      this.isMuted.set(false);
    }
  }

  toggleMute(): void {
    const video = this.videoPlayerRef()?.nativeElement;
    if (!video) return;

    video.muted = !video.muted;
    this.isMuted.set(video.muted);
  }

  toggleFullscreen(): void {
    const video = this.videoPlayerRef()?.nativeElement;
    if (!video) return;

    if (!document.fullscreenElement) {
      video.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  // ============================================================================
  // Controls Visibility
  // ============================================================================

  showControls(): void {
    this.controlsVisible.set(true);
    if (this.isPlaying()) {
      this.startControlsHideTimer();
    }
  }

  onMouseMove(): void {
    this.showControls();
  }

  onMouseLeave(): void {
    this.qualityMenuOpen.set(false);
    if (this.isPlaying()) {
      this.startControlsHideTimer();
    }
  }

  /** Focus arrived in the player: reveal the bar exactly as pointer motion does. */
  onFocusIn(): void {
    this.focusWithinControls = true;
    this.showControls();
  }

  /** Focus left the player (a `relatedTarget` outside it): the auto-hide timer resumes. */
  onFocusOut(event: FocusEvent): void {
    const host = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    this.focusWithinControls = next !== null && host.contains(next);
    if (!this.focusWithinControls && this.isPlaying()) {
      this.startControlsHideTimer();
    }
  }

  private startControlsHideTimer(): void {
    this.clearControlsTimeout();
    this.controlsTimeout = setTimeout(() => {
      if (this.isPlaying() && !this.focusWithinControls) {
        this.controlsVisible.set(false);
        this.qualityMenuOpen.set(false);
      }
    }, 3000);
  }

  private clearControlsTimeout(): void {
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
      this.controlsTimeout = null;
    }
  }

  // ============================================================================
  // Helpers
  // ============================================================================

}
